import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { fetchAllPages } from "./supabasePaging";
import { CaseTaskError } from "./caseTasks";
import { subscribeWithResync, uniqueChannelName } from "./realtimeResync";
import { isMissingRelationError } from "./supabaseErrors";

/** Une ligne de `public.sales_team_members`. */
export interface TeamMember {
  center: string;
  profile_id: string;
  created_at: string;
  created_by: string | null;
}

export interface TeamProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
}

export interface TeamMemberWithProfile extends TeamMember {
  profile: TeamProfile | null;
}

interface TeamContextType {
  members: TeamMember[];
  /** Tous les profils internes (pour choisir qui ajouter à une équipe). */
  profiles: TeamProfile[];
  profilesById: Map<string, TeamProfile>;
  /** Centres qui ont au moins un membre, triés. */
  centers: string[];
  centersOfUser: (profileId: string | null | undefined) => string[];
  membersOfCenter: (center: string) => TeamMemberWithProfile[];
  /** Manager uniquement (RLS). Lève une CaseTaskError (message français). */
  addMember: (center: string, profileId: string) => Promise<void>;
  removeMember: (center: string, profileId: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

const MEMBER_SELECT = "center,profile_id,created_at,created_by";

const sameMember = (a: Pick<TeamMember, "center" | "profile_id">, b: Pick<TeamMember, "center" | "profile_id">) =>
  a.center === b.center && a.profile_id === b.profile_id;

function upsertMember(members: TeamMember[], row: TeamMember): TeamMember[] {
  return members.some((member) => sameMember(member, row))
    ? members.map((member) => (sameMember(member, row) ? row : member))
    : [...members, row];
}

export function TeamProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [profiles, setProfiles] = useState<TeamProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Table sales_team_members absente (front déployé avant la migration) : aucune équipe, sans realtime. */
  const [teamUnavailable, setTeamUnavailable] = useState(false);
  const requestVersion = useRef(0);
  const inFlight = useRef(false);
  const dirtyDuringLoad = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    const version = ++requestVersion.current;
    inFlight.current = true;
    dirtyDuringLoad.current = false;
    try {
      let membersMissing = false;
      const [memberRows, profileRows] = await Promise.all([
        fetchAllPages<TeamMember>((from, to) =>
          supabase
            .from("sales_team_members")
            .select(MEMBER_SELECT)
            .order("center")
            .order("profile_id")
            .range(from, to),
        ).catch((err: unknown) => {
          if (!isMissingRelationError(err)) throw err;
          console.warn("[Team] Table sales_team_members absente (migration en attente) : aucune équipe.");
          membersMissing = true;
          return [] as TeamMember[];
        }),
        fetchAllPages<TeamProfile>((from, to) =>
          supabase.from("profiles").select("id,email,full_name,role").order("id").range(from, to),
        ),
      ]);
      if (version !== requestVersion.current) return;
      setMembers(memberRows);
      setProfiles(profileRows);
      setError(null);
      if (membersMissing) setTeamUnavailable(true);
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error("[Team] Chargement des équipes impossible :", err);
      setError("Impossible de charger les équipes.");
    } finally {
      if (version === requestVersion.current) {
        inFlight.current = false;
        setLoading(false);
        // Un événement realtime est arrivé pendant le chargement : on recharge pour ne pas l'écraser.
        if (dirtyDuringLoad.current) void load();
      }
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      requestVersion.current += 1;
      setMembers([]);
      setProfiles([]);
      setLoading(false);
      return;
    }
    if (teamUnavailable) return; // profils déjà chargés ; rien à écouter
    setLoading(true);
    void load();

    const channel = supabase.channel(uniqueChannelName("sales-team-members")).on(
      "postgres_changes",
      { event: "*", schema: "public", table: "sales_team_members" },
      (payload) => {
        if (inFlight.current) dirtyDuringLoad.current = true;
        if (payload.eventType === "DELETE") {
          const old = payload.old as Partial<TeamMember>;
          if (old.center && old.profile_id) {
            setMembers((prev) => prev.filter((member) => !sameMember(member, old as TeamMember)));
          } else {
            void load();
          }
          return;
        }
        setMembers((prev) => upsertMember(prev, payload.new as TeamMember));
      },
    );
    return subscribeWithResync(channel, () => void load());
  }, [authLoading, userId, teamUnavailable, load]);

  const addMember = useCallback(async (center: string, profileId: string) => {
    const cleanCenter = center.trim();
    const { data, error: insertError } = await supabase
      .from("sales_team_members")
      .insert({ center: cleanCenter, profile_id: profileId })
      .select(MEMBER_SELECT)
      .single();
    if (insertError) throw new CaseTaskError(insertError);
    if (data) setMembers((prev) => upsertMember(prev, data as TeamMember));
  }, []);

  const removeMember = useCallback(async (center: string, profileId: string) => {
    const { data, error: deleteError } = await supabase
      .from("sales_team_members")
      .delete()
      .eq("center", center)
      .eq("profile_id", profileId)
      .select("center,profile_id");
    if (deleteError) throw new CaseTaskError(deleteError);
    // RLS : un non-manager ne « voit » aucune ligne à supprimer.
    if (!data || data.length === 0) throw new CaseTaskError({ message: "not_manager" });
    setMembers((prev) => prev.filter((member) => !sameMember(member, { center, profile_id: profileId })));
  }, []);

  const value = useMemo<TeamContextType>(() => {
    const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));
    const centers = [...new Set(members.map((member) => member.center))].sort((a, b) => a.localeCompare(b, "fr"));
    return {
      members,
      profiles,
      profilesById,
      centers,
      centersOfUser: (profileId) =>
        profileId ? members.filter((member) => member.profile_id === profileId).map((member) => member.center) : [],
      membersOfCenter: (center) =>
        members
          .filter((member) => member.center === center)
          .map((member) => ({ ...member, profile: profilesById.get(member.profile_id) ?? null }))
          .sort((a, b) =>
            (a.profile?.full_name || a.profile?.email || "").localeCompare(
              b.profile?.full_name || b.profile?.email || "",
              "fr",
            ),
          ),
      addMember,
      removeMember,
      loading: authLoading || loading,
      error,
      refresh: load,
    };
  }, [members, profiles, addMember, removeMember, authLoading, loading, error, load]);

  return createElement(TeamContext.Provider, { value }, children);
}

export function useTeam(): TeamContextType {
  const context = useContext(TeamContext);
  if (!context) throw new Error("useTeam doit être utilisé dans un TeamProvider");
  return context;
}
